import React from 'react';

import Link from '@dashboard/components/mui-atoms/Link';
import Header from '@dashboard/components/pages/QueryListPage/PageWrapper/Header';
import { createTableComponent } from '@dashboard/components/pages/QueryListPage/TableList/WpDataGrid';
import {
  type FDomainNameVerificationFragment,
  useDomainNameVerificationsQuery,
  usePlatformQuery,
} from '@dashboard/generated/wp/administrator';
import { useCurrentPage } from '@dashboard/hooks/route/useCurrentPage';
import { Paper, Typography } from '@mui/material';
import useQueryDataTableProps from '@wp-react/hooks/useQueryDataTableProps';

import { DateFormatter } from '@waypoint/react-formatters';

function PlatformLink({ platformId }: { platformId: string }) {
  const { data } = usePlatformQuery({ variables: { id: platformId } });
  const name = data?.Platform.name;
  if (!name) {
    return null;
  }
  return <Link href={`/o/${platformId}`}>{name}</Link>;
}

const DomainNameVerificationsTable = createTableComponent<FDomainNameVerificationFragment>({
  emptyResults: <Typography>No domain name verifications found.</Typography>,
  columns: [
    {
      headerName: 'Actions',
      minWidth: 96,
      maxWidth: 96,
      renderCell: ({ row }) => <Link href={`/admin/o/${row.platformId}`}>Admin</Link>,
    },
    { headerName: 'Domain ID', renderCell: ({ row: { id } }) => <Link href={`/admin/domains/${id}`}>{id}</Link> },
    { headerName: 'Domain name', renderCell: ({ row }) => <Typography>{row.domainName}</Typography> },
    {
      headerName: 'Platform',
      renderCell: ({ row }) => <PlatformLink platformId={row.platformId} />,
    },
    { headerName: 'Status', renderCell: ({ row }) => <Typography>{row.status}</Typography> },
    { headerName: 'Approval', renderCell: ({ row }) => <Typography>{row.approvalStatus}</Typography> },
    {
      headerName: 'Created at',
      renderCell: ({ row }) => (
        <Typography>
          <DateFormatter value={row.createdAt} />
        </Typography>
      ),
    },
  ],
});

export default function DomainNameVerificationsPage() {
  const currentPage = useCurrentPage();
  const { data } = useDomainNameVerificationsQuery({ variables: { page: currentPage } });
  const { pagination } = useQueryDataTableProps(data?.DomainNameVerifications);
  const nodes = data?.DomainNameVerifications.nodes;

  return (
    <Paper>
      <Header title="Domain name verifications" pagination={pagination} />
      <DomainNameVerificationsTable nodes={nodes} />
    </Paper>
  );
}
